import type { Problem } from '@verydia/contracts';

/**
 * Every non-2xx response becomes a `VerydiaApiError`. The important property is that
 * `code` and `requestId` survive onto the thrown object: an integrator debugging a
 * failure should never have to re-read the response body to find either.
 */
export class VerydiaApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | null;
  readonly problem: Problem | null;

  /** Seconds the server asked the caller to wait, from `Retry-After`. */
  readonly retryAfterSeconds: number | null;

  constructor(init: {
    message: string;
    status: number;
    code: string;
    requestId: string | null;
    problem?: Problem | null;
    retryAfterSeconds?: number | null;
  }) {
    super(init.message);
    this.name = 'VerydiaApiError';
    this.status = init.status;
    this.code = init.code;
    this.requestId = init.requestId;
    this.problem = init.problem ?? null;
    this.retryAfterSeconds = init.retryAfterSeconds ?? null;
  }

  /**
   * The control plane is waking from idle.
   *
   * Distinct from a failure: the right response is to wait and retry, and to
   * tell the user something is starting rather than something is broken.
   */
  get isStarting(): boolean {
    return this.code === 'database_starting';
  }

  /** True when retrying the identical request could plausibly succeed. */
  get isRetryable(): boolean {
    return this.status === 429 || this.status >= 500;
  }
}

/** Network-level failure: no HTTP response was received at all. */
export class VerydiaConnectionError extends Error {
  override readonly cause: unknown;
  constructor(message: string, cause: unknown) {
    super(message);
    this.name = 'VerydiaConnectionError';
    this.cause = cause;
  }
}
